import React, { useState, useEffect, useRef } from 'react';
import { playRewardSound } from '../lib/rewardSound';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Search, Wrench, Plus, Car, Calendar, DollarSign, X, ChevronDown, CheckCircle, Check, Trash2, Link as LinkIcon, AlertCircle, Unlink, ShieldCheck } from 'lucide-react';

const approvedAnimStyles = `
@keyframes stampDrop {
  0%   { transform: scale(4) rotate(-20deg); opacity: 0; }
  50%  { transform: scale(0.9) rotate(4deg); opacity: 1; }
  70%  { transform: scale(1.08) rotate(-2deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}
@keyframes fadeInOverlay {
  0%   { opacity: 0; }
  100% { opacity: 1; }
}
@keyframes fadeOutOverlay {
  0%   { opacity: 1; }
  100% { opacity: 0; }
}
@keyframes particlePop {
  0%   { transform: translate(0, 0) scale(0); opacity: 1; }
  100% { transform: translate(var(--tx), var(--ty)) scale(1.5); opacity: 0; }
}
@keyframes ringExpand {
  0%   { transform: scale(0.3); opacity: 1; }
  100% { transform: scale(4); opacity: 0; }
}
@keyframes checkPulse {
  0%   { transform: scale(0); opacity: 0; }
  60%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
`;

const SERVICE_TYPES = [
  'Oil Change', 'Oil Filter', 'Air Filter', 'Cabin Filter',
  'Brake Pads', 'Brake Discs', 'Spark Plugs', 'Timing Belt',
  'Battery Replacement', 'Tire Rotation', 'Wheel Alignment',
  'Transmission Fluid', 'Coolant Flush', 'Car Inspection',
  'Insurance', 'AC Service', 'Suspension', 'Other',
];

const ServiceDashboard = () => {
  const { user } = useAuth();
  const [searchPlate, setSearchPlate] = useState('');
  const [selectedCar, setSelectedCar] = useState(null);
  const [maintenanceLogs, setMaintenanceLogs] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [deleteConfirmLogId, setDeleteConfirmLogId] = useState(null);
  const [showApproved, setShowApproved] = useState(false);
  const approvedTimerRef = useRef(null);

  const [pendingRequests, setPendingRequests] = useState([]);
  const [subscribedCars, setSubscribedCars] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    if (user) loadLinks();
  }, [user]);

  const loadLinks = async () => {
    setLoadingLinks(true);
    const { data: pending } = await supabase
      .from('car_service_links')
      .select('id, car_id, status, cars(id, name, plate_number, color, total_mileage_km)')
      .eq('service_id', user.id)
      .eq('status', 'pending');

    const { data: subscribed } = await supabase
      .from('car_service_links')
      .select('id, car_id, status, cars(id, name, plate_number, color, total_mileage_km)')
      .eq('service_id', user.id)
      .eq('status', 'approved');

    const { count } = await supabase
      .from('service_maintenance_logs')
      .select('id', { count: 'exact', head: true })
      .eq('service_user_id', user.id);

    setPendingRequests(pending || []);
    setSubscribedCars(subscribed || []);
    setTotalRecords(count || 0);
    setLoadingLinks(false);
  };

  const handleLinkAction = async (linkId, newStatus) => {
    const { error } = await supabase
      .from('car_service_links')
      .update({ status: newStatus })
      .eq('id', linkId);

    if (!error) {
      loadLinks();
      if (newStatus === 'approved') setSuccessMsg('Car successfully linked!');
      else if (newStatus === 'rejected') setSuccessMsg('Link request rejected.');
      setTimeout(() => setSuccessMsg(''), 3000);
    }
  };

  const handleUnlinkCar = async (linkId) => {
    if (!window.confirm('Are you sure you want to unlink this car?')) return false;
    const { error } = await supabase
      .from('car_service_links')
      .delete()
      .eq('id', linkId);
    if (!error) {
      loadLinks();
      setSuccessMsg('Car unlinked successfully.');
      setTimeout(() => setSuccessMsg(''), 3000);
      return true;
    }
    return false;
  };

  // New maintenance form state
  const [newLog, setNewLog] = useState({
    serviceType: SERVICE_TYPES[0],
    description: '',
    mileage: '',
    cost: '',
    serviceDate: new Date().toISOString().slice(0, 10),
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const cardStyle = {
    background: 'var(--bg-secondary)',
    borderRadius: '16px',
    padding: '1.25rem',
    marginBottom: '0.75rem',
    border: '1px solid var(--border-color)',
  };

  // Compute search results directly — no state, no async, no bugs
  const searchQuery = searchPlate.trim().toLowerCase().replace(/\s+/g, '');
  const searchResults = searchQuery
    ? subscribedCars
      .map(req => {
        const car = Array.isArray(req.cars) ? req.cars[0] : req.cars;
        return car ? { ...car, link_id: req.id } : null;
      })
      .filter(Boolean)
      .filter(car => {
        const plate = (car.plate_number || '').toLowerCase().replace(/\s+/g, '');
        const name = (car.name || '').toLowerCase().replace(/\s+/g, '');
        return plate.includes(searchQuery) || name.includes(searchQuery);
      })
    : [];


  const selectCar = async (car) => {
    setSelectedCar(car);
    // Load maintenance logs for this car's unique ID
    const { data: logs } = await supabase
      .from('service_maintenance_logs')
      .select('*')
      .eq('car_id', car.id)
      .eq('service_user_id', user.id)
      .order('service_date', { ascending: false });
    setMaintenanceLogs(logs || []);
  };

  const handleAddMaintenance = async () => {
    if (!selectedCar) return;
    setFormError('');
    setSubmitting(true);

    try {
      const { error } = await supabase
        .from('service_maintenance_logs')
        .insert({
          service_user_id: user.id,
          car_id: selectedCar.id,
          car_plate_number: selectedCar.plate_number,
          service_type: newLog.serviceType,
          description: newLog.description || null,
          mileage_at_service: newLog.mileage ? parseInt(newLog.mileage) : null,
          cost: newLog.cost ? parseFloat(newLog.cost) : null,
          service_date: newLog.serviceDate,
        });

      if (error) throw error;

      // Refresh logs
      const { data: logs } = await supabase
        .from('service_maintenance_logs')
        .select('*')
        .eq('car_id', selectedCar.id)
        .eq('service_user_id', user.id)
        .order('service_date', { ascending: false });
      setMaintenanceLogs(logs || []);

      setNewLog({
        serviceType: SERVICE_TYPES[0],
        description: '',
        mileage: '',
        cost: '',
        serviceDate: new Date().toISOString().slice(0, 10),
      });
      setShowAddForm(false);
      // Trigger stamp animation
      if (approvedTimerRef.current) clearTimeout(approvedTimerRef.current);
      setShowApproved(true);
      setTimeout(() => playRewardSound(), 250);
      approvedTimerRef.current = setTimeout(() => setShowApproved(false), 2000);
    } catch (err) {
      setFormError(err.message || 'Failed to add record');
    } finally {
      setSubmitting(false);
    }
  };

  const requestDelete = (logId) => {
    setDeleteConfirmLogId(logId);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmLogId) return;
    try {
      const { error } = await supabase
        .from('service_maintenance_logs')
        .delete()
        .eq('id', deleteConfirmLogId)
        .eq('service_user_id', user.id);

      if (error) throw error;

      setMaintenanceLogs(logs => logs.filter(l => l.id !== deleteConfirmLogId));
      setSuccessMsg('Record deleted successfully.');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      console.error('Error deleting record:', err);
      // Maybe show an error
      alert('Failed to delete record: ' + err.message);
    } finally {
      setDeleteConfirmLogId(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmLogId(null);
  };

  const inputStyle = {
    width: '100%', padding: '0.75rem',
    border: '1px solid var(--border-color)', borderRadius: '10px',
    background: 'var(--bg-primary)', color: 'var(--text-primary)',
    fontSize: '0.95rem', boxSizing: 'border-box',
  };

  return (
    <div style={{ padding: '0.5rem' }}>
      {/* Inject keyframe styles once */}
      <style>{approvedAnimStyles}</style>

      {/* Approved Stamp Overlay - fullscreen white */}
      {showApproved && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            background: '#ffffff',
            animation: 'fadeInOverlay 0.3s ease both, fadeOutOverlay 0.4s ease 1.6s forwards',
            pointerEvents: 'none',
          }}
        >
          {/* Expanding rings */}
          {[0, 1].map(i => (
            <div key={i} style={{
              position: 'absolute',
              width: '200px', height: '200px',
              borderRadius: '50%',
              border: `${3 - i}px solid #22c55e`,
              opacity: 0.5 - i * 0.15,
              animation: `ringExpand 1.2s ease-out ${0.1 + i * 0.3}s both`,
            }} />
          ))}

          {/* Particles */}
          {[
            { tx: '-130px', ty: '-120px', color: '#22c55e', size: '14px' },
            { tx: '130px', ty: '-120px', color: '#4ade80', size: '10px' },
            { tx: '-130px', ty: '120px', color: '#86efac', size: '12px' },
            { tx: '130px', ty: '120px', color: '#22c55e', size: '10px' },
            { tx: '0px', ty: '-160px', color: '#4ade80', size: '8px' },
            { tx: '0px', ty: '160px', color: '#86efac', size: '14px' },
            { tx: '-165px', ty: '0px', color: '#22c55e', size: '10px' },
            { tx: '165px', ty: '0px', color: '#4ade80', size: '8px' },
            { tx: '-100px', ty: '-150px', color: '#bbf7d0', size: '8px' },
            { tx: '100px', ty: '150px', color: '#bbf7d0', size: '8px' },
          ].map((p, i) => (
            <div key={i} style={{
              position: 'absolute',
              width: p.size, height: p.size,
              borderRadius: '50%',
              background: p.color,
              '--tx': p.tx, '--ty': p.ty,
              animation: `particlePop 1s ease-out ${0.2 + i * 0.04}s both`,
            }} />
          ))}

          {/* Icon */}
          <div style={{
            width: '120px', height: '120px',
            borderRadius: '50%',
            background: '#22c55e',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 60px rgba(34,197,94,0.35)',
            animation: 'checkPulse 0.7s cubic-bezier(0.22,1,0.36,1) 0.15s both',
            marginBottom: '2rem',
          }}>
            <ShieldCheck size={60} color="white" strokeWidth={1.5} />
          </div>

          <div style={{
            color: '#15803d', fontWeight: 900,
            fontSize: '2.5rem', letterSpacing: '0.12em',
            textTransform: 'uppercase',
            animation: 'stampDrop 0.8s cubic-bezier(0.22,1,0.36,1) 0.3s both',
          }}>
            Approved
          </div>
          <div style={{
            color: '#6b7280', fontSize: '1rem',
            fontWeight: 500, marginTop: '0.5rem',
            animation: 'fadeInOverlay 0.6s ease 0.7s both',
          }}>
            Maintenance record saved
          </div>
        </div>
      )}
      {/* Header */}
      <div style={{ marginBottom: '1rem' }}>
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.3rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Wrench size={22} /> Service Dashboard
        </h2>
        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          Look up customer cars and add maintenance records
        </p>
      </div>

      {/* Stat Boxes */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem', marginBottom: '1rem' }}>
            {[
              {
                label: 'Linked Cars',
                value: loadingLinks ? '—' : subscribedCars.length,
                icon: <Car size={20} strokeWidth={2.5} />,
                gradient: 'linear-gradient(135deg, #60A5FA 0%, #1D4ED8 100%)',
                glow: 'rgba(37, 99, 235, 0.35)',
              },
              {
                label: 'Records Added',
                value: loadingLinks ? '—' : totalRecords,
                icon: <Wrench size={20} strokeWidth={2.5} />,
                gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                glow: 'rgba(17,153,142,0.35)',
              },
              {
                label: 'Pending',
                value: loadingLinks ? '—' : pendingRequests.length,
                icon: <AlertCircle size={20} strokeWidth={2.5} />,
                gradient: pendingRequests.length > 0
                  ? 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)'
                  : 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
                glow: pendingRequests.length > 0 ? 'rgba(247,151,30,0.35)' : 'rgba(107,114,128,0.2)',
              },
            ].map(({ label, value, icon, gradient, glow }) => (
              <div
                key={label}
                style={{
                  borderRadius: '16px',
                  padding: '1rem 0.6rem',
                  background: gradient,
                  boxShadow: `0 8px 16px -4px ${glow}`,
                  border: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'transform 0.2s ease',
                  color: 'white',
                }}
              >
                <div style={{
                  width: '38px', height: '38px', borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  backdropFilter: 'blur(4px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', flexShrink: 0,
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}>
                  {icon}
                </div>
                <div style={{ fontWeight: 800, fontSize: '1.5rem', lineHeight: 1, color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  {value}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.95)', fontWeight: 600, textAlign: 'center', lineHeight: 1.2, textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                  {label}
                </div>
              </div>
            ))}
          </div>

          {/* Search Bar */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={18} style={{
                position: 'absolute', left: '0.75rem', top: '50%',
                transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none',
              }} />
              <input
                type="text"
                value={searchPlate}
                onChange={e => { setSearchPlate(e.target.value); setSelectedCar(null); setMaintenanceLogs([]); }}
                placeholder="Search car name or plate..."
                style={{
                  ...inputStyle,
                  paddingLeft: '2.5rem',
                  fontWeight: 600,
                }}
              />
            </div>
            <button
              onClick={() => { }}
              style={{
                padding: '0 1.25rem', border: 'none', borderRadius: '10px',
                background: '#738ADB',
                color: 'white', fontWeight: 600, cursor: 'default',
                opacity: 0.5,
                display: 'none',
              }}
            >
              Search
            </button>
          </div>

          {/* Success Message */}
          {successMsg && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.75rem 1rem', background: '#dcfce7', borderRadius: '10px',
              marginBottom: '0.75rem', color: '#166534', fontSize: '0.9rem', fontWeight: 500,
            }}>
              <CheckCircle size={18} /> {successMsg}
            </div>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && !selectedCar && (
            <div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontWeight: 600 }}>
                {searchResults.length} car(s) found
              </div>
              {searchResults.map(car => (
                <button
                  key={car.id}
                  onClick={() => selectCar(car)}
                  style={{
                    ...cardStyle,
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    width: '100%', cursor: 'pointer', textAlign: 'left',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '12px',
                    background: car.color || '#738ADB',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Car size={22} color="white" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{car.name || 'Car'}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                      {car.plate_number}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {searchQuery && searchResults.length === 0 && !selectedCar && (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--text-secondary)' }}>
              <Car size={48} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
              <p style={{ margin: '0 0 0.25rem', fontWeight: 600 }}>No cars found</p>
              <p style={{ margin: 0, fontSize: '0.85rem' }}>No linked car matches "{searchPlate.trim()}"</p>
            </div>
          )}

          {/* Selected Car Detail */}
          {selectedCar && (
            <div>
              {/* Car Info */}
              <div style={{
                ...cardStyle,
                background: '#738ADB22, rgba(118, 75, 162, 0.1))',
                border: '1px solid rgba(99, 102, 241, 0.3)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '14px',
                      background: selectedCar.color || '#738ADB',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Car size={24} color="white" />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{selectedCar.name || 'Car'}</div>
                      <div style={{
                        fontSize: '0.85rem', color: '#738ADB', fontFamily: 'monospace',
                        fontWeight: 600, letterSpacing: '0.05em',
                      }}>
                        {selectedCar.plate_number?.toUpperCase()}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {selectedCar.link_id && (
                      <button
                        onClick={async () => {
                          const unlinked = await handleUnlinkCar(selectedCar.link_id);
                          if (unlinked) {
                            setSelectedCar(null);
                            setMaintenanceLogs([]);
                          }
                        }}
                        title="Unlink car"
                        style={{
                          background: 'rgba(239, 68, 68, 0.1)', border: 'none', cursor: 'pointer',
                          color: '#ef4444', padding: '6px', borderRadius: '8px', display: 'flex'
                        }}
                      >
                        <Unlink size={18} />
                      </button>
                    )}
                    <button
                      onClick={() => { setSelectedCar(null); setMaintenanceLogs([]); }}
                      style={{
                        background: 'rgba(0,0,0,0.05)', border: 'none', cursor: 'pointer',
                        color: 'var(--text-secondary)', padding: '6px', borderRadius: '8px', display: 'flex'
                      }}
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Add Maintenance Button */}
              {!showAddForm && (
                <button
                  onClick={() => setShowAddForm(true)}
                  style={{
                    width: '100%', padding: '0.85rem',
                    background: '#738ADB',
                    border: 'none', borderRadius: '12px', color: 'white',
                    fontWeight: 600, cursor: 'pointer', fontSize: '0.95rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                    marginBottom: '1rem',
                  }}
                >
                  <Plus size={18} /> Add Maintenance Record
                </button>
              )}

              {/* Add Maintenance Form */}
              {showAddForm && (
                <div style={{ ...cardStyle, marginBottom: '1rem' }}>
                  <h3 style={{ margin: '0 0 1rem', fontWeight: 700, fontSize: '1rem' }}>
                    New Maintenance Record
                  </h3>

                  {formError && (
                    <div style={{ color: '#ef4444', fontSize: '0.85rem', padding: '0.5rem', background: '#fee2e2', borderRadius: '8px', marginBottom: '0.75rem' }}>
                      {formError}
                    </div>
                  )}

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
                      Service Type *
                    </label>
                    <select
                      value={newLog.serviceType}
                      onChange={e => setNewLog(p => ({ ...p, serviceType: e.target.value }))}
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      {SERVICE_TYPES.map(st => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
                      Description
                    </label>
                    <input
                      type="text"
                      value={newLog.description}
                      onChange={e => setNewLog(p => ({ ...p, description: e.target.value }))}
                      placeholder="e.g., Castrol 5W-30, synthetic"
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
                        Mileage
                      </label>
                      <input
                        type="number"
                        value={newLog.mileage}
                        onChange={e => setNewLog(p => ({ ...p, mileage: e.target.value }))}
                        placeholder="km"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
                        Cost
                      </label>
                      <input
                        type="number"
                        value={newLog.cost}
                        onChange={e => setNewLog(p => ({ ...p, cost: e.target.value }))}
                        placeholder="0.00"
                        step="0.01"
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-secondary)' }}>
                      Service Date *
                    </label>
                    <input
                      type="date"
                      value={newLog.serviceDate}
                      onChange={e => setNewLog(p => ({ ...p, serviceDate: e.target.value }))}
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button
                      onClick={() => { setShowAddForm(false); setFormError(''); }}
                      style={{
                        flex: 1, padding: '0.75rem', border: '1px solid var(--border-color)',
                        borderRadius: '10px', background: 'transparent',
                        color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddMaintenance}
                      disabled={submitting}
                      style={{
                        flex: 1, padding: '0.75rem',
                        background: submitting ? '#9ca3af' : '#738ADB',
                        border: 'none', borderRadius: '10px', color: 'white',
                        fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {submitting ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              )}

              {/* Maintenance History */}
              <div style={{ marginBottom: '0.5rem' }}>
                <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Your Maintenance Records ({maintenanceLogs.length})
                </h3>

                {maintenanceLogs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--text-secondary)' }}>
                    <Wrench size={36} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>No records yet. Add one above!</p>
                  </div>
                ) : (
                  maintenanceLogs.map(log => (
                    <div key={log.id} style={{
                      ...cardStyle,
                      display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                    }}>
                      <div style={{
                        width: '36px', height: '36px', borderRadius: '10px',
                        background: 'rgba(99, 102, 241, 0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Wrench size={18} color="#738ADB" />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{log.service_type}</div>
                          <button
                            onClick={() => requestDelete(log.id)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)', border: 'none', cursor: 'pointer',
                              color: '#ef4444', padding: '6px', borderRadius: '6px', display: 'flex'
                            }}
                            title="Delete record"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        {log.description && (
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                            {log.description}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '0.35rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <Calendar size={12} /> {log.service_date}
                          </span>
                          {log.mileage_at_service && (
                            <span>{log.mileage_at_service.toLocaleString()} km</span>
                          )}
                          {log.cost && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <DollarSign size={12} /> {parseFloat(log.cost).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Empty State - no search yet, show subscribed cars */}
          {!searchQuery && !selectedCar && (
            <div style={{ marginTop: '1.5rem' }}>

              {/* Pending Requests */}
              {pendingRequests.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#eab308' }}>
                    <AlertCircle size={18} /> Pending Link Requests ({pendingRequests.length})
                  </h3>
                  {pendingRequests.map(req => {
                    const car = Array.isArray(req.cars) ? req.cars[0] : req.cars;
                    if (!car) return null;
                    return (
                      <div key={req.id} style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: car.color || '#738ADB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Car size={20} color="white" />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600 }}>{car.name}</div>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{car.plate_number}</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button onClick={() => handleLinkAction(req.id, 'rejected')} style={{ padding: '0.5rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                            <X size={18} />
                          </button>
                          <button onClick={() => handleLinkAction(req.id, 'approved')} style={{ padding: '0.5rem', background: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                            <Check size={18} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Subscribed Cars */}
              {subscribedCars.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-primary)' }}>
                    <LinkIcon size={18} /> Subscribed Cars ({subscribedCars.length})
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                    {subscribedCars.map(req => {
                      const car = Array.isArray(req.cars) ? req.cars[0] : req.cars;
                      if (!car) return null;
                      return (
                        <div
                          key={req.id}
                          style={{
                            ...cardStyle,
                            display: 'flex', alignItems: 'center', gap: '0.75rem',
                            margin: 0
                          }}
                        >
                          <div
                            onClick={() => selectCar({ ...car, link_id: req.id })}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, cursor: 'pointer' }}
                          >
                            <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: car.color || '#738ADB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Car size={22} color="white" />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '1.05rem' }}>{car.name || 'Car'}</div>
                            </div>
                            <div style={{
                              padding: '0.25rem 0.5rem',
                              background: 'var(--bg-secondary)',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              fontFamily: 'monospace',
                              color: 'var(--text-primary)',
                              whiteSpace: 'nowrap'
                            }}>
                              {car.plate_number ? car.plate_number.toUpperCase() : 'NO PLATE'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Original empty state */}
              {pendingRequests.length === 0 && subscribedCars.length === 0 && !loadingLinks && (
                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-secondary)' }}>
                  <Search size={48} style={{ opacity: 0.2, marginBottom: '1rem' }} />
                  <p style={{ margin: '0 0 0.5rem', fontWeight: 600, fontSize: '1.05rem' }}>Search for a car</p>
                  <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.5 }}>
                    Enter a license plate number to view car details and add maintenance records
                  </p>
                </div>
              )}
            </div>
          )}
        {/* Delete Confirmation Modal */}
      {deleteConfirmLogId && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'var(--bg-primary)',
            padding: '1.5rem',
            borderRadius: '16px',
            maxWidth: '90%',
            width: '320px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            border: '1px solid var(--border-color)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '50%',
              background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <AlertCircle size={24} />
            </div>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', color: 'var(--text-primary)' }}>Confirm Delete</h3>
            <p style={{ margin: '0 0 1.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Are you sure you want to delete this maintenance record?
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={cancelDelete}
                style={{
                  flex: 1, padding: '0.75rem', borderRadius: '10px',
                  background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer'
                }}>
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  flex: 1, padding: '0.75rem', borderRadius: '10px',
                  background: '#ef4444', border: 'none',
                  color: 'white', fontWeight: 600, cursor: 'pointer'
                }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDashboard;
